/// <amd-module name="Repos/Views/PullRequest/PullRequestPermissionService" />
import { GitPullRequest, GitVersionDescriptor, GitVersionOptions, GitVersionType } from "Repos/Common/Contracts/Git";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";

export const enum PullRequestPermissions {
    AbandonReactivate,
    AddAttachments,
    AddEditComment,
    CancelAutoComplete,
    CherryPickRevert,
    Complete,
    DeleteSourceBranch,
    Follow,
    LikeComment,
    LiveUpdate,
    PublishUnpublish,
    PushToSourceBranch,
    RestartMerge,
    Retarget,
    Share,
    UpdateCommentStatus,
    UpdateFavorites,
    UpdateLabels,
    UpdateReviewers,
    UpdateTitleDescription,
    UpdateVisit,
    UpdateWorkItems,
    UsePolicyActions,
    ViewFavorites,
    Vote
}

export interface IPullRequestPermissionService extends IVssService {
    /**
     * Determine whether the current user has specified pull request permission.
     *
     * @param checkedPermission Pull request permission to check
     */
    hasPermission(checkedPermission: PullRequestPermissions): boolean;

    /**
     * Sets the permissions for the given pull request.
     *
     * @param pullRequest Pull request details
     */
    setPermisssions(pullRequest: GitPullRequest): void;
}

function getBranchDescriptor(branchRefName: string): GitVersionDescriptor {
    return {
        version: refToBranchName(branchRefName),
        versionOptions: GitVersionOptions.None,
        versionType: GitVersionType.Branch
    };
}

class PullRequestPermissionService extends VssService implements IPullRequestPermissionService {
    private permissions = new Map<PullRequestPermissions, boolean>();

    public hasPermission(checkedPermission: PullRequestPermissions): boolean {
        if (!this.permissions.has(checkedPermission)) {
            throw new Error("Unknown permission. Make sure permissions are set.");
        }

        return this.permissions.get(checkedPermission)!;
    }

    public setPermisssions(pullRequest: GitPullRequest): void {
        const { permissions } = this;

        permissions.clear();

        const permissionsService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");

        const canContributeToPullRequest = this.canContributeToPullRequest(permissionsService, pullRequest);
        const canContributeToTargetBranch = this.canContributeToTargetBranch(permissionsService, pullRequest);
        const canCreateBranch = this.canCreateBranch(permissionsService, pullRequest);
        const canDeleteSourceBranch = this.canDeleteSourceBranch(permissionsService, pullRequest);
        const canReadFavorites = permissionsService.hasFavoritesPermission(1 /* Read */);
        const canWriteFavorites = permissionsService.hasFavoritesPermission(2 /* Write */);
        const canWriteSettings = permissionsService.hasSettingsPermission(2 /* Write */);

        permissions.set(PullRequestPermissions.AbandonReactivate, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.AddAttachments, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.AddEditComment, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.CancelAutoComplete, canContributeToPullRequest && canContributeToTargetBranch);
        permissions.set(PullRequestPermissions.CherryPickRevert, canContributeToPullRequest && canCreateBranch);
        permissions.set(PullRequestPermissions.Complete, canContributeToPullRequest && canContributeToTargetBranch);
        permissions.set(PullRequestPermissions.DeleteSourceBranch, canContributeToPullRequest && canDeleteSourceBranch);
        permissions.set(PullRequestPermissions.Follow, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.LikeComment, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.LiveUpdate, canWriteSettings);
        permissions.set(PullRequestPermissions.PublishUnpublish, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.PushToSourceBranch, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.RestartMerge, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.Retarget, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.Share, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.UpdateCommentStatus, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.UpdateFavorites, canWriteFavorites);
        permissions.set(PullRequestPermissions.UpdateLabels, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.UpdateReviewers, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.UpdateTitleDescription, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.UpdateVisit, canWriteSettings);
        permissions.set(PullRequestPermissions.UpdateWorkItems, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.UsePolicyActions, canContributeToPullRequest);
        permissions.set(PullRequestPermissions.ViewFavorites, canReadFavorites);
        permissions.set(PullRequestPermissions.Vote, canContributeToPullRequest);
    }

    private canContributeToPullRequest(permissionsService: IReposPermissionsService, pullRequest: GitPullRequest): boolean {
        return permissionsService.hasPermission(
            GitRepositoryPermissions.PullRequestContribute,
            undefined,
            pullRequest.repository.id,
            pullRequest.repository.project.id
        );
    }

    private canContributeToTargetBranch(permissionsService: IReposPermissionsService, pullRequest: GitPullRequest): boolean {
        return permissionsService.hasPermission(
            GitRepositoryPermissions.GenericContribute,
            getBranchDescriptor(pullRequest.targetRefName),
            pullRequest.repository.id,
            pullRequest.repository.project.id
        );
    }

    private canCreateBranch(permissionsService: IReposPermissionsService, pullRequest: GitPullRequest): boolean {
        return permissionsService.hasPermission(
            GitRepositoryPermissions.CreateBranch,
            undefined,
            pullRequest.repository.id,
            pullRequest.repository.project.id
        );
    }

    private canDeleteSourceBranch(permissionsService: IReposPermissionsService, pullRequest: GitPullRequest): boolean {
        const sourceBranchRefName = pullRequest.forkSource ? pullRequest.forkSource.name : pullRequest.sourceRefName;
        const sourceRepositoryId = pullRequest.forkSource ? pullRequest.forkSource.repository.id : pullRequest.repository.id;
        const projectId =
            pullRequest.forkSource && pullRequest.forkSource.repository.project
                ? pullRequest.forkSource.repository.project.id
                : pullRequest.repository.project.id;

        return (
            permissionsService.hasPermission(
                GitRepositoryPermissions.GenericContribute,
                getBranchDescriptor(sourceBranchRefName),
                sourceRepositoryId,
                projectId
            ) &&
            permissionsService.hasPermission(
                GitRepositoryPermissions.ForcePush,
                getBranchDescriptor(sourceBranchRefName),
                sourceRepositoryId,
                projectId
            )
        );
    }
}

const serviceName = "IPullRequestPermissionService";

function getPullRequestPermissionService(pageContext: IVssPageContext): IPullRequestPermissionService {
    return pageContext.getService<IPullRequestPermissionService>(serviceName);
}

/**
 * Determine whether the current user has specified pull request permission.
 *
 * @param pageContext Page context
 * @param checkedPermission Pull request permission to check
 */
export function hasPermission(pageContext: IVssPageContext, checkedPermission: PullRequestPermissions): boolean {
    return getPullRequestPermissionService(pageContext).hasPermission(checkedPermission);
}

/**
 * Sets the permissions for the given pull request.
 *
 * @param pageContext Page context
 * @param pullRequest Pull request details
 */
export function setPermissions(pageContext: IVssPageContext, pullRequest: GitPullRequest): void {
    getPullRequestPermissionService(pageContext).setPermisssions(pullRequest);
}

Services.add(serviceName, { serviceFactory: PullRequestPermissionService });
